import qs from 'query-string';
import moment from 'moment';
import { useCallback, useEffect, useReducer } from 'react';
import axios from 'axios';

import i18n, { translateCell } from '../../i18n';
import { loginModel } from '../../constants/models';
import { getErrors } from '../../utils';
import { showNotification } from '../../components/common';
import { notificationTypes, actions, languages, MAX_EXCEL_RECORDS } from '../../constants';
import {
  removeAccessToken,
  removeUsername,
  removeRefreshToken,
  removeFullname,
  removeUserStatus,
  removeStakeholderType,
  removeStakeholderTypeId,
  getAllAuthRequestFields,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  getAccessToken
} from '../authentication';
import { exportAsCSV } from '../../hooks/exports';
import { REACT_APP_RSD_DOMAIN } from '../../api/base';

import { resolveOnNoContent } from './resolvers';


const saveToken = response => {
  const token = response.data[loginModel.ACCESS_TOKEN];
  const refreshToken = response.data[loginModel.REFRESH_TOKEN] ?? response.data[loginModel.NEW_REFRESH_TOKEN];

  setAccessToken(token);
  setRefreshToken(refreshToken);

  return getAllAuthRequestFields();
};

const getRequestedParams = () => ({
  url: `${REACT_APP_RSD_DOMAIN}user-service/api/portal/Refresh/acces-token`,
  method: 'GET',
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    'refreshToken': getRefreshToken()
  }
});


export const refreshJwtAsync = async() => {

  const response = await axios(getRequestedParams()).then(r => {
    saveToken(r);

    return true;
  }).catch(() => false);

  return response;

};

export const parseJwt = token => {
  if (!token) {
    return null;
  }
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(atob(base64).split('')
    .map(c => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
    .join(''));

  return JSON.parse(jsonPayload);
};


export const validateToken = async() => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (accessToken) {
    const accessJwt = parseJwt(accessToken);
    if (!accessJwt) { return false; }
    const exp = accessJwt.exp;
    const now = Date.now() / 1000;

    if (exp < now) {

      const refreshJwt = parseJwt(refreshToken);
      const refreshExp = refreshJwt.exp;

      if (refreshExp < now) {

        return false;
      }
      const response = await refreshJwtAsync();

      return response;
    }


    return true;
  }

  return false;


};
const portal = window.location.pathname.split('/')[1];
const logOut = () => {
  removeUsername();
  removeRefreshToken();
  removeAccessToken();
  removeFullname();
  removeUserStatus();
  removeStakeholderType();
  removeStakeholderTypeId();
  window.location.replace(`/${portal}/login`);
};
const appErrHandler = ({ response }) => {
  const container = response?.data?.container;
  const message = response?.data?.message;
  let title;
  if (container && typeof (container) === 'string') {
    title = container;
  } else if (message && typeof (message) === 'string') {
    title = message;
  } else {
    title = translateCell(`ERR.${getErrors(response)}`);
  }
  showNotification({
    title,
    type: notificationTypes.ERROR
  });

  return response;
};
const sendRequest = async({ path, options }) => {
  const { origin, pathname } = new URL(path);
  let { search } = new URL(path);
  const { method, query, data, form } = options;
  const publicAPIs = [
    'user-service/api/portal/Login/LoginUser',
    'user-service/api/portal/Register/smp-company-stakeholder',
    'user-service/api/portal/UserActions/mp-reset-password',
    'user-service/api/portal/UserActions/reset-password',
    'user-service/api/portal/UserActions/submit-recovery-password',
    'user-service/api/portal/UserActions/sop-reset-password',
    'user-service/api/portal/UserActions/smp-reset-password',
    'user-service/api/portal/UserActions/submit-smp-recovery-password'
  ];

  if (!publicAPIs.some(p => pathname.includes(p))
  ) {

    // validate token
    const isValidToken = await validateToken();
    if (!isValidToken) {
      logOut();

    }
  }


  if (method && method.toUpperCase() === 'GET' && query) {
    const urlQueryString = qs.stringify(query);
    if (urlQueryString) {
      search = search ? `${search}&${urlQueryString}` : `?${urlQueryString}`;
    }
  }
  const url = `${origin}${pathname}${search}`;
  const defaultHeaders = {
    'Accept-Language': (i18n.language === languages.EN ? 'en' : 'ar')
  };

  return axios({
    url,
    method,
    credentials: 'same-origin',
    mode: 'cors',
    cache: 'default',
    headers: {
      ...defaultHeaders,
      ...getAllAuthRequestFields()
      // portal: 'smp'
    },
    data: data || form || {}
  })
    .then(response => resolveOnNoContent({ response }))
    .then(async response => response)
    .catch(e => {
      if (e?.response?.status && parseInt(e?.response?.status) === 401) {
        return Promise.reject(e);
      } else if (e?.response?.status && parseInt(e?.response?.status) === 413) {
        return Promise.reject(appErrHandler({ response: { status: 413, msg: e.message } }));
      } else if (e?.response?.data?.responseCode) {
        return Promise.reject(appErrHandler({ response: e.response }));
      } else if (e.status) {
        return Promise.reject(appErrHandler({ response: { status: e.status } }));
      }

      return Promise.reject(appErrHandler({ response: { status: 1234, msg: e.message } }));

      // return Promise.reject(e);
    });
};
const fetchApi = () => async(path, options) =>
  sendRequest({
    path,
    options
  });


export const handleApiRequest = ({ path, query, method,
  notJson, data, additionalHeader = {}, form }) =>
  fetchApi()(
    path,
    {
      headers: { ...getAllAuthRequestFields(), ...additionalHeader },
      method,
      query,
      notJson,
      data,
      form
    }
  );


const fetchReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case actions.REQUEST:
      return {
        ...state,
        isFetching: true,
        isFailed: false,
        isSuccess: false
      };
    case actions.SUCCESS:
      return {
        ...state,
        isFetching: false,
        isSuccess: true,
        data: payload
      };
    case actions.FAILED:
      return {
        ...state,
        isFetching: false,
        isFailed: true,
        error: payload
      };
    case actions.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    case actions.CLEAR_ALL:
      return initialFetchState;
    case actions.CANCELED:
      return state;
    default:
      throw new Error();
  }
};


const initialFetchState = {
  isFetching: false,
  isFailed: false,
  isSuccess: false,
  data: [],
  error: null
};


const dispatcher = ({ fetcher, dataTransformer, dispatch }) => async(...params) => {
  try {
    dispatch({ type: actions.REQUEST });
    const response = await fetcher(...params);
    if (!response) {
      return Promise.reject(response);
    }

    const payload = dataTransformer(response);
    dispatch({ type: actions.SUCCESS, payload });

    return Promise.resolve(payload);
  } catch (error) {
    if (error && error.message === 'Canceled by client') {
      dispatch({ type: actions.CANCELED, payload: error });

      return Promise.resolve(error);
    }
    dispatch({ type: actions.FAILED, payload: error });

    return Promise.reject(error);
  }
};


export const useFetch = (fetcher, dataTransformer = d => d.data) => {
  const [ state, dispatch ] = useReducer(fetchReducer, initialFetchState);
  const fetchData = dispatcher({ fetcher, dataTransformer, dispatch });
  function clearError() {
    dispatch({ type: actions.CLEAR_ERROR });
  }
  function clearAll() {
    dispatch({ type: actions.CLEAR_ALL });
  }

  return [
    state,
    fetchData,
    { clearError, clearAll }
  ];
};

export const useExcel = (fetcher, formValues, total, title, columns, subColumns = null,
  csv = false, useSemicolon = false) => {
  const [ { data: excelData, isSuccess, isFetching, isFailed }, fetchExcel ] = useFetch(fetcher,
    useCallback(response => ({
      items: response?.data?.container?.rows
    })));


  const onFetchExcel = useCallback(query => {
    fetchExcel(query);
  }, [ fetchExcel ]);

  useEffect(() => {

    if (!isSuccess) { return; }


    let combinedColumns = columns;
    if (subColumns) {
      combinedColumns = columns.concat(subColumns);
    }

    exportAsCSV(excelData.items, title, combinedColumns, csv, useSemicolon);

  }, [ isSuccess ]);

  const onGenerateExcel = () => {
    if (!total) { return; }
    if (formValues) {
      onFetchExcel({
        ...formValues,
        page: 0,
        perPage: total < MAX_EXCEL_RECORDS ? total : MAX_EXCEL_RECORDS
      });
    } else {
      onFetchExcel({
        page: 0,
        perPage: total < MAX_EXCEL_RECORDS ? total : MAX_EXCEL_RECORDS
      });
    }


  };

  return [ { isSuccess, isFetching, isFailed }, onGenerateExcel ];

};
